import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

interface ToastContextValue {
  show(message: string, durationMs?: number): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** App-wide bottom-centered toast. Rendered into document.body via createPortal
 *  with inline styles so it is immune to any parent stacking contexts, CSS-module
 *  scoping quirks, or ancestor CSS variables that might swallow the background.
 *  Styles mirror the vanilla admin toast exactly. Used by both the admin panel
 *  and the voice-talent pages. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string>('');
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<number | null>(null);

  const show = useCallback((msg: string, durationMs = 2000) => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    setMessage(msg);
    setVisible(true);
    hideTimer.current = window.setTimeout(() => setVisible(false), durationMs);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              bottom: '32px',
              left: '50%',
              transform: visible
                ? 'translateX(-50%) translateY(0)'
                : 'translateX(-50%) translateY(20px)',
              background: 'var(--fg)',
              color: 'var(--bg)',
              padding: '12px 24px',
              borderRadius: 6,
              fontFamily: 'var(--font-family)',
              fontSize: 12,
              opacity: visible ? 1 : 0,
              transition: 'opacity 0.25s, transform 0.25s',
              pointerEvents: 'none',
              zIndex: 99999,
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            }}
          >
            {message}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
